import moment from "moment";

export const DICTIONARY = {
    qualityLevel: {
        'EXCELLENT': { text: 'Excelente', class: 'excellent' },
        'GOOD': { text: 'Bom', class: 'good' },
        'REGULAR': { text: 'Regular', class: 'regular' },
        'POOR': { text: 'Ruim', class: 'poor' }
    },
    severity: {
        'HIGH': { text: 'Crítico', class: 'critical', icon: 'fa-exclamation' },
        'MEDIUM': { text: 'Médio', class: 'medium', icon: 'fa-exclamation' },
        'LOW': { text: 'Baixo', class: 'low', icon: 'fa-exclamation' }
    },
    ruleStatus: {
        'COMPLIANT': { text: 'Conforme', class: 'compliant', icon: 'fa-check' },
        'VIOLATED': { text: 'Violada', class: 'violated', icon: 'fa-times' },
        'PARTIAL': { text: 'Parcial', class: 'partial', icon: 'fa-exclamation' }
    },
    priority: {
        'HIGH': { text: 'Alta', class: 'high' },
        'MEDIUM': { text: 'Média', class: 'medium' },
        'LOW': { text: 'Baixa', class: 'low' }
    },
    signalType: {
        'RULE_VIOLATION': 'Violação de Regra',
        'NEGATIVE_PATTERN': 'Padrão Negativo',
        'POSITIVE_PATTERN': 'Padrão Positivo',
        'OPPORTUNITY': 'Oportunidade',
        'RISK': 'Risco',
        'DATA_QUALITY': 'Qualidade de dados'
    }
};

export const RULE_LABELS = {
    R1: 'Frequência de prato',
    R2: 'Frequência de grupo',
    R3: 'Não repetir no mesmo dia',
    R4: 'Ingredientes consecutivos',
    R5: 'Complexidade',
    R6: 'Pre-preparo',
    R7: 'Utilização do grupo',
    R8: 'Combinação obrigatória',
    R9: 'Combinação proibida',
    R10: 'Lacuna',
    R11: 'Custo per capita acima da meta'
};

// ====== Formatação ======

export function formatDate(dateString) {
    if (!dateString) return '';
    try {
        return normalizeDateForMenu(dateString) || dateString;
    } catch {
        return dateString;
    }
}

export function formatDateTime(dateTimeString) {
    if (!dateTimeString) return '';
    try {
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR');
    } catch {
        return dateTimeString;
    }
}

export function formatScenarioPeriod(period, t) {
    if (!period) return '-';
    const start = parsePeriodDate(period.startDate);
    const end = parsePeriodDate(period.endDate);
    if (!start) return '-';

    const capitalize = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
    const startLabel = capitalize(start.clone().locale('pt-br').format('MMMM [de] YYYY'));

    if (!end || start.isSame(end, 'month')) {
        return startLabel;
    }

    const endLabel = capitalize(end.clone().locale('pt-br').format('MMMM [de] YYYY'));
    return `${startLabel} ${t('to')} ${endLabel}`;
}

export function parsePeriodDate(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const parsed = moment(raw, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
    if (parsed.isValid()) return parsed;

    const fallback = moment(raw);
    return fallback.isValid() ? fallback : null;
}

export function normalizeDateForMenu(dateString) {
    if (!dateString) return null;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        return dateString;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    }
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('pt-BR');
}

export function getScoreClass(score) {
    if (score >= 90) return 'excellent';
    if (score >= 70) return 'good';
    if (score >= 50) return 'regular';
    return 'poor';
}

export function getDictionary(key, type, property = 'text') {
    if (!key || !type) return key || '';
    const item = DICTIONARY[type]?.[key];
    if (!item) return key;
    if (property && typeof item === 'object') {
        return item[property] || key;
    }
    return typeof item === 'string' ? item : item.text || key;
}

export function resolveRuleLabel(ruleId) {
    return RULE_LABELS[ruleId] || ruleId || '-';
}

export function formatCriterion(value, t) {
    const map = {
        ZERO_USAGE: t('relatorio_criterio_zero_usage'),
        MAX_MONTH: t('relatorio_criterio_max_month'),
        MIN_MONTH: t('relatorio_criterio_min_month'),
        MAX_WEEK: t('relatorio_criterio_max_week')
    };
    return map[value] || value || '-';
}

export function formatScope(scope, week) {
    if (!scope) return '-';
    if (scope === 'WEEK') return week ? `Semana: ${week}` : 'Semana';
    return 'Mês';
}

export function formatR5ContextMode(mode) {
    if (mode === 'DAY_TYPE') return 'Por tipo';
    if (mode === 'DAY_GLOBAL') return 'Global do dia';
    return '-';
}

export function formatPercent(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return `${(numeric * 100).toFixed(1)}%`;
}

export function formatCurrency(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(numeric);
}

export function formatNumber(value, fractionDigits = 2) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '-';
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(numeric);
}

export function formatCategory(category) {
    if (!category) return '-';
    const normalized = String(category).toUpperCase();
    const map = {
        ABOVE_MAX: 'Acima do máximo',
        NON_USED: 'Não utilizado',
        BELOW_MIN: 'Abaixo do mínimo'
    };
    return map[normalized] || normalized;
}

export function formatR9ConflictType(conflictType) {
    if (!conflictType) return '-';
    const normalized = String(conflictType).toUpperCase();
    const map = {
        PRATO_PRATO: 'Prato ↔ Prato',
        PRATO_GRUPO: 'Prato ↔ Grupo',
        GRUPO_PRATO: 'Grupo ↔ Prato',
        GRUPO_GRUPO: 'Grupo ↔ Grupo'
    };
    return map[normalized] || normalized;
}

export function formatUsage(usage, t) {
    if (!usage) return '-';
    const parsed = parseUsage(usage);
    const parts = [
        `${t('relatorio_utilizado')}: ${parsed.used}`,
        `${t('relatorio_minimo')}: ${parsed.min}`,
        `${t('relatorio_maximo')}: ${parsed.max}`
    ];
    if (parsed.delta !== null) {
        parts.push(`${t('relatorio_diferenca')}: ${parsed.delta}`);
    }
    return parts.join(' | ');
}

export function parseUsage(usage) {
    if (!usage) {
        return { used: 0, min: '-', max: '-', delta: null };
    }
    return {
        used: usage.used ?? 0,
        min: usage.min ?? '-',
        max: usage.max ?? '-',
        delta: usage.delta ?? null
    };
}

export function buildUsageRow(label, usage) {
    return {
        label,
        type: 'usage',
        usage: parseUsage(usage)
    };
}

export function buildEntityRow(label, entity, options = {}) {
    const parsed = extractEntityCodeName(entity);
    const shouldShowCode = shouldShowEntityCode(entity);
    return {
        label,
        type: 'entity',
        code: shouldShowCode && parsed.code !== '-' ? parsed.code : null,
        name: parsed.name,
        nameTitle: parsed.name !== '-' ? parsed.name : null,
        codeLabel: options.codeLabel !== false,
        singleLine: !!options.singleLine
    };
}

export function formatColumnDisplay(column) {
    if (!column) return '-';

    const rawLabel = String(column.name || column.label || column.id || '').trim();
    if (!rawLabel) return '-';

    const suffixMatch = rawLabel.match(/(\d+)\s*$/);
    if (suffixMatch) return suffixMatch[1];

    const normalized = rawLabel.replace(/^coluna\s*:?/i, '').trim();
    if (normalized) return normalized;

    const rawId = column.id !== undefined && column.id !== null ? String(column.id).trim() : '';
    return rawId || rawLabel;
}

export function formatTokenLabel(value) {
    if (!value) return '-';
    const parsed = String(value);
    if (parsed.includes('|')) {
        return parsed.split('|').slice(1).join('|').trim();
    }
    return parsed;
}

export function extractEntityCodeName(entity) {
    if (!entity) return { code: '-', name: '-' };

    if (typeof entity === 'string' || typeof entity === 'number') {
        const labelOnly = String(entity);
        if (labelOnly.includes('|')) {
            const left = labelOnly.split('|')[0];
            const code = left.includes(':') ? left.split(':').pop() : left;
            const name = labelOnly.split('|').slice(1).join('|').trim();
            return { code: code || '-', name: name || '-' };
        }
        return { code: '-', name: labelOnly };
    }

    const rawId = entity.id !== undefined && entity.id !== null ? String(entity.id) : '';
    const rawName = entity.name ? String(entity.name) : '';
    const label = entity.label ? String(entity.label) : '';

    let code = rawId;
    if (code.includes(':')) {
        code = code.split(':').pop();
    }
    if (!code && label && label.includes('|')) {
        const left = label.split('|')[0];
        code = left.includes(':') ? left.split(':').pop() : left;
    }

    let name = rawName;
    if (!name && label && label.includes('|')) {
        name = label.split('|').slice(1).join('|').trim();
    }
    if (!name && label && label !== '-') {
        name = label;
    }

    return {
        code: code || '-',
        name: name || '-'
    };
}

export function formatBoolean(value) {
    if (value === true) return 'Sim';
    if (value === false) return 'Não';
    return '-';
}

export function normalizeSequencial(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized.length ? normalized : null;
}

export function normalizePlateId(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized.length ? normalized : null;
}

export function normalizeHighlightType(value) {
    if (!value) return 'conflict';
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'usage' || normalized === 'candidate' || normalized === 'conflict' || normalized === 'solver-user') {
        return normalized;
    }
    return 'conflict';
}

export function getActionHighlightType(action) {
    if (!action || typeof action !== 'object') return 'conflict';
    if (action.highlightType) {
        return normalizeHighlightType(action.highlightType);
    }
    if (Array.isArray(action.highlightTargets) && action.highlightTargets.length > 0) {
        return normalizeHighlightType(action.highlightTargets[0]?.highlightType || 'conflict');
    }
    return 'conflict';
}

export function getActionButtonClass(action) {
    const type = getActionHighlightType(action);
    return {
        'navigate-btn-usage': type === 'usage',
        'navigate-btn-candidate': type === 'candidate',
        'navigate-btn-conflict': type === 'conflict'
    };
}

export function getActionLabel(action, t, fallbackKey = 'relatorio_ir_para_cardapio') {
    if (action?.label) return action.label;
    return t(fallbackKey);
}

function normalizeReasonLabel(reasonLabel) {
    if (typeof reasonLabel !== 'string') return null;
    const normalized = reasonLabel.trim();
    return normalized.length ? normalized : null;
}

function normalizeReasonKey(reasonKey) {
    if (typeof reasonKey !== 'string') return null;
    const normalized = reasonKey.trim();
    return normalized.length ? normalized : null;
}

export function buildHighlightTarget(date, sequencial, plateId, highlightType = 'conflict', metadata = null) {
    const normalizedDate = normalizeDateForMenu(date);
    if (!normalizedDate) return null;
    const normalizedReasonLabel = normalizeReasonLabel(metadata?.reasonLabel);
    const normalizedReasonKey = normalizeReasonKey(metadata?.reasonKey);
    const normalizedOrigin = metadata?.origin || null;
    return {
        date: normalizedDate,
        sequencial: normalizeSequencial(sequencial),
        plateId: normalizePlateId(plateId),
        highlightType: normalizeHighlightType(highlightType),
        reasonLabel: normalizedReasonLabel,
        reasonKey: normalizedReasonKey,
        reasonLabels: normalizedReasonLabel ? [normalizedReasonLabel] : [],
        origin: normalizedOrigin
    };
}

export function normalizeHighlightTarget(target, fallbackType = 'conflict') {
    if (!target) return null;
    return buildHighlightTarget(
        target.date,
        target.sequencial,
        target.plateId,
        target.highlightType || fallbackType,
        {
            reasonLabel: target.reasonLabel,
            reasonKey: target.reasonKey,
            origin: target.origin
        }
    );
}

export function dedupeTargets(targets = []) {
    const uniq = new Map();
    targets.forEach((target) => {
        const normalized = normalizeHighlightTarget(target);
        if (!normalized) return;
        const key = [
            normalized.date,
            normalized.sequencial || '-',
            normalized.plateId || '-',
            normalized.highlightType || 'conflict'
        ].join('::');
        if (!uniq.has(key)) {
            uniq.set(key, normalized);
            return;
        }

        const existing = uniq.get(key);
        const labels = new Set([...(existing.reasonLabels || [])]);
        if (normalized.reasonLabel) labels.add(normalized.reasonLabel);

        const mergedLabels = Array.from(labels);
        uniq.set(key, {
            ...existing,
            reasonLabel: existing.reasonLabel || normalized.reasonLabel || null,
            reasonKey: existing.reasonKey || normalized.reasonKey || null,
            reasonLabels: mergedLabels,
            origin: existing.origin || normalized.origin || null
        });
    });
    return Array.from(uniq.values());
}

export function buildTargetsFromIssueTargets(targets, fallbackPlateId = null, highlightType = 'conflict', metadata = null) {
    if (!Array.isArray(targets)) return [];
    const normalizedTargets = targets.map((target) => buildHighlightTarget(
        target?.date,
        target?.sequencial,
        target?.plateId ?? fallbackPlateId,
        highlightType,
        metadata
    ));
    return dedupeTargets(normalizedTargets);
}

// ====== Construção de targets específicos ======

export function buildR1UsageTargets(entity, t) {
    const occurrences = Array.isArray(entity?.occurrences) ? entity.occurrences : [];
    const plateId = entity?.plate?.id || null;
    const plateName = extractEntityCodeName(entity?.plate).name || t('relatorio_entidade_prato');
    const reasonLabel = t('relatorio_highlight_utilizacao_de', { PARAM: plateName });
    const targets = occurrences.map((occ) => buildHighlightTarget(
        occ?.date,
        occ?.sequencial,
        plateId,
        'usage',
        {
            reasonLabel,
            reasonKey: `R1:usage:${plateId || plateName}`,
            origin: 'action'
        }
    ));
    return dedupeTargets(targets);
}

export function buildR1CandidateTargets(entity, t) {
    const plateId = entity?.plate?.id || null;
    const plateName = extractEntityCodeName(entity?.plate).name || t('relatorio_entidade_prato');
    return buildTargetsFromIssueTargets(
        entity?.candidateTargets,
        plateId,
        'candidate',
        {
            reasonLabel: t('relatorio_highlight_oportunidade_para_adicionar', { PARAM: plateName }),
            reasonKey: `R1:candidate:${plateId || plateName}`,
            origin: 'action'
        }
    );
}

export function buildR2UsageTargets(groupEntry, t) {
    const groupInfo = extractEntityCodeName(groupEntry?.group);
    const groupName = groupEntry?.groupName || groupInfo.name || t('group_of_dishes');
    return buildTargetsFromIssueTargets(
        groupEntry?.occurrences,
        null,
        'usage',
        {
            reasonLabel: t('relatorio_highlight_utilizacao_de', { PARAM: groupName }),
            reasonKey: `R2:usage:${groupEntry?.groupCode || groupInfo.code || groupName}`,
            origin: 'action'
        }
    );
}

export function buildR2CandidateTargets(groupEntry, t) {
    const groupInfo = extractEntityCodeName(groupEntry?.group);
    const groupName = groupEntry?.groupName || groupInfo.name || t('group_of_dishes');
    const metadata = {
        reasonLabel: t('relatorio_highlight_oportunidade_para_adicionar', { PARAM: groupName }),
        reasonKey: `R2:candidate:${groupEntry?.groupCode || groupInfo.code || groupName}`,
        origin: 'action'
    };
    const candidateCells = Array.isArray(groupEntry?.candidateCells) ? groupEntry.candidateCells : [];
    if (candidateCells.length) {
        const cellTargets = candidateCells.map((cell) => buildHighlightTarget(
            cell?.date,
            cell?.sequencial,
            null,
            'candidate',
            metadata
        ));
        return dedupeTargets(cellTargets);
    }

    const legacyTargets = buildTargetsFromIssueTargets(
        groupEntry?.candidateTargets,
        null,
        'candidate',
        metadata
    );
    const byCell = new Map();
    legacyTargets.forEach((target) => {
        const key = [target.date, target.sequencial || '-'].join('::');
        if (!byCell.has(key)) {
            byCell.set(key, {
                ...target,
                plateId: null
            });
        }
    });
    return Array.from(byCell.values());
}

export function buildR4Targets(date, plates, fallbackSequentials, metadata = null) {
    const result = [];
    const safePlates = Array.isArray(plates) ? plates : [];
    const safeSequentials = Array.isArray(fallbackSequentials) ? fallbackSequentials : [];

    safePlates.forEach((plate, index) => {
        const sequencial = plate?.sequencial ?? safeSequentials[index] ?? null;
        result.push(buildHighlightTarget(date, sequencial, plate?.idPrato, 'conflict', metadata));
    });

    if (!safePlates.length && safeSequentials.length) {
        safeSequentials.forEach((sequencial) => {
            result.push(buildHighlightTarget(date, sequencial, null, 'conflict', metadata));
        });
    }

    if (!result.length) {
        result.push(buildHighlightTarget(date, null, null, 'conflict', metadata));
    }

    return dedupeTargets(result);
}

export function buildR8CandidateTargets(entity, mode = 'required', t) {
    const pivotName = extractEntityCodeName(entity?.pivot).name || t('relatorio_entidade_pivo');
    const missingItems = Array.isArray(entity?.missingItems) ? entity.missingItems : [];
    const requiredRefs = mode === 'pivot'
        ? []
        : (missingItems.length
            ? missingItems
            : [entity?.requiredItem || null].filter(Boolean));

    if (mode === 'required' && requiredRefs.length) {
        const targets = Array.isArray(entity?.candidateTargets) ? entity.candidateTargets : [];
        const fallbackRequiredNames = requiredRefs.map((requiredRef) =>
            extractEntityCodeName(requiredRef).name || t('relatorio_entidade_item_obrigatorio')
        );
        const expandedTargets = targets.flatMap((target) => {
            const targetRequiredName = getR8TargetRequiredName(target);
            const candidateNames = targetRequiredName ? [targetRequiredName] : fallbackRequiredNames;
            return candidateNames.map((requiredName) => buildHighlightTarget(
                target?.date,
                target?.sequencial,
                target?.plateId ?? null,
                'candidate',
                {
                    reasonLabel: t('relatorio_r8_oportunidade_para', { PARAM: requiredName }),
                    reasonKey: `R8:${mode}:candidate:${requiredName}`,
                    origin: 'action'
                }
            ));
        });
        return dedupeTargets(expandedTargets);
    }

    const reasonLabel = t('relatorio_r8_oportunidade_para_adicionar', { PARAM: pivotName });
    return buildTargetsFromIssueTargets(
        entity?.candidateTargets,
        null,
        'candidate',
        {
            reasonLabel,
            reasonKey: `R8:${mode}:candidate:${entity?.pivot?.id || pivotName}`,
            origin: 'action'
        }
    );
}

export function getR8TargetRequiredName(target) {
    const rawReason = normalizeReasonLabel(target?.reason);
    if (!rawReason) return null;
    if (['AVAILABLE_REQUIRED_IN_COLUMN', 'AVAILABLE_PIVOT_IN_COLUMN'].includes(rawReason)) {
        return null;
    }
    if (rawReason.includes('|')) {
        const [, rawName] = rawReason.split('|');
        const normalizedName = rawName?.trim();
        if (normalizedName) return normalizedName;
    }
    return rawReason;
}

export function buildR9ConflictTargets(entity, t) {
    const targets = Array.isArray(entity?.conflictTargets) ? entity.conflictTargets : [];
    if (!targets.length) return [];

    const normalizedTargets = targets.map((target, index) => {
        const conflictName = getR9ConflictCounterpartName(entity, target, t)
            || t('relatorio_destacar_conflito');
        return buildHighlightTarget(
            target?.date,
            target?.sequencial,
            target?.plateId,
            'conflict',
            {
                reasonLabel: t('relatorio_r9_conflito_com', { PARAM: conflictName }),
                reasonKey: `R9:conflict:${target?.reason || target?.sequencial || index}`,
                origin: 'action'
            }
        );
    });

    return dedupeTargets(normalizedTargets);
}

export function buildR5ContextTargets(context, t) {
    const reasonLabel = t('relatorio_r5_highlight_prato_alta_complexidade');
    return buildTargetsFromIssueTargets(
        context?.highComplexityTargets,
        null,
        'usage',
        {
            reasonLabel,
            reasonKey: `R5:${context?.date || 'unknown'}:${context?.mode || 'unknown'}:${context?.tipo || 'global'}`,
            origin: 'action'
        }
    );
}

// ====== R3 tags ======

export function buildR3Tags(entity, t) {
    const tags = [];
    const repetitionRefs = entity?.repetitionRefs || {};
    const fallbackAction = entity?.date ? { date: entity.date } : null;

    const appendTag = (label, refs) => {
        const highlightTargets = buildTargetsFromRepetitionRefs(
            refs,
            entity?.date,
            'conflict',
            {
                reasonLabel: label,
                reasonKey: label,
                origin: 'tag'
            }
        );
        tags.push({
            label,
            highlightTargets,
            action: highlightTargets.length ? null : fallbackAction
        });
    };

    (entity?.repetitions?.cores || []).forEach((color) => {
        const refs = getR3RepetitionRefsByToken(repetitionRefs?.cores, color);
        appendTag(t('cor_repetida', { PARAM: color }), refs);
    });

    (entity?.repetitions?.proteinas || []).forEach((protein) => {
        const refs = getR3RepetitionRefsByToken(repetitionRefs?.proteinas, protein);
        appendTag(
            t('proteina_repetida', { PARAM: formatTokenLabel(protein) }),
            refs
        );
    });

    (entity?.repetitions?.modosPreparo || []).forEach((prep) => {
        const refs = getR3RepetitionRefsByToken(repetitionRefs?.modosPreparo, prep);
        appendTag(
            t('modopreparo_repetido', { PARAM: formatTokenLabel(prep) }),
            refs
        );
    });

    (entity?.repetitions?.ingredientes || []).forEach((ingredient) => {
        const refs = getR3RepetitionRefsByToken(repetitionRefs?.ingredientes, ingredient);
        appendTag(
            t('ingrediente_repetido', { PARAM: formatTokenLabel(ingredient) }),
            refs
        );
    });

    return tags;
}

export function buildR3AllTargets(entity, preparedTags = null) {
    const tags = Array.isArray(preparedTags) ? preparedTags : buildR3Tags(entity);
    const allTargets = tags
        .filter(tag => Array.isArray(tag?.highlightTargets))
        .flatMap(tag => tag.highlightTargets);

    const targets = dedupeTargets(allTargets);
    if (targets.length) return targets;

    if (entity?.date) {
        const fallback = buildHighlightTarget(
            entity.date,
            null,
            null,
            'conflict',
            {
                reasonLabel: 'Destacar conflitos',
                reasonKey: 'r3-fallback-conflict',
                origin: 'action'
            }
        );
        return fallback ? [fallback] : [];
    }

    return [];
}

export function getR3RepetitionRefsByToken(groupRefs, token) {
    if (!groupRefs || typeof groupRefs !== 'object') return [];
    const direct = Array.isArray(groupRefs[token]) ? groupRefs[token] : [];
    if (direct.length) return direct;

    const normalizedToken = formatTokenLabel(token);
    const matchedKey = Object.keys(groupRefs).find(
        key => formatTokenLabel(key) === normalizedToken
    );
    if (!matchedKey) return [];

    return Array.isArray(groupRefs[matchedKey]) ? groupRefs[matchedKey] : [];
}

export function buildTargetsFromRepetitionRefs(refs, fallbackDate, highlightType = 'conflict', metadata = null) {
    if (!Array.isArray(refs)) return [];
    const targets = refs.map((ref) => buildHighlightTarget(
        ref?.date || fallbackDate,
        ref?.sequencial,
        ref?.plateId ?? ref?.idPrato,
        highlightType,
        metadata
    ));
    return dedupeTargets(targets);
}

// ====== Identificação e agrupamento ======

export function getIssueRule(issue, entity) {
    return entity?.rule || issue?.ruleReference || null;
}

export function shouldRenderEntity(issue, entity) {
    const ruleId = getIssueRule(issue, entity);
    if (ruleId !== 'R2') return true;

    const count = Number(entity?.count ?? 0);
    const hasGroups = Array.isArray(entity?.groups) && entity.groups.length > 0;
    return count > 0 || hasGroups;
}

export function getVisibleEntities(issue) {
    const entities = Array.isArray(issue?.affectedEntities) ? issue.affectedEntities : [];
    return entities.filter(entity => shouldRenderEntity(issue, entity));
}

export function getEntitiesCount(issue) {
    return getVisibleEntities(issue).length;
}

export function hasEntities(issue) {
    return getEntitiesCount(issue) > 0;
}

export function isR1Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R1';
}

export function isR2Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R2';
}

export function getR2EntityKey(issue, issueIndex, entity, entityIndex = 0) {
    const issueKey = getIssueKey(issue, issueIndex);
    const category = typeof entity?.category === 'string' && entity.category.trim().length
        ? entity.category.trim().toUpperCase()
        : `entity-${entityIndex}`;
    return `${issueKey}::${category}`;
}

export function getIssueKey(issue, index) {
    return issue?.issueId || `issue-${index}`;
}

export function isR3Issue(issue) {
    if (issue?.ruleReference === 'R3') return true;
    if (!Array.isArray(issue?.affectedEntities)) return false;
    return issue.affectedEntities.some(entity => getIssueRule(issue, entity) === 'R3');
}

export function isR3Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R3';
}

export function getR3EntityScope(entity) {
    const normalized = String(entity?.scope || '').trim().toUpperCase();
    if (normalized === 'BY_TYPE' || normalized === 'GLOBAL_DAY') return normalized;
    return entity?.tipo ? 'BY_TYPE' : 'GLOBAL_DAY';
}

export function isR3ByTypeEntity(entity) {
    return getR3EntityScope(entity) === 'BY_TYPE';
}

export function getR3EntityGroups(issue) {
    const entities = getVisibleEntities(issue)
        .filter(entity => getIssueRule(issue, entity) === 'R3');
    const byType = entities.filter(entity => getR3EntityScope(entity) === 'BY_TYPE');
    const globalDay = entities.filter(entity => getR3EntityScope(entity) === 'GLOBAL_DAY');
    const sortEntities = list => [...list].sort((a, b) => {
        const dateA = normalizeDateForMenu(a?.date) || formatDate(a?.date) || '';
        const dateB = normalizeDateForMenu(b?.date) || formatDate(b?.date) || '';
        const compareDate = dateA.localeCompare(dateB);
        if (compareDate !== 0) return compareDate;
        return String(a?.tipo || '').localeCompare(String(b?.tipo || ''));
    });

    const groups = [
        {
            groupKey: 'BY_TYPE',
            label: 'Por tipo',
            entities: sortEntities(byType)
        },
        {
            groupKey: 'GLOBAL_DAY',
            label: 'Global do dia',
            entities: sortEntities(globalDay)
        }
    ];

    return groups.filter(group => group.entities.length > 0);
}

export function getRealEntityIndex(issue, entity) {
    const entities = Array.isArray(issue?.affectedEntities) ? issue.affectedEntities : [];
    return entities.indexOf(entity);
}

export function hasR3GroupedEntities(issue) {
    return getR3EntityGroups(issue).length > 0;
}

export function getR3GroupStorageKey(issue, issueIndex, groupKey) {
    return `${getIssueKey(issue, issueIndex)}::${groupKey}`;
}

export function isR8Issue(issue) {
    if (issue?.ruleReference === 'R8') return true;
    if (!Array.isArray(issue?.affectedEntities)) return false;
    return issue.affectedEntities.some(entity => getIssueRule(issue, entity) === 'R8');
}

export function isR8UnmetEntity(issue, entity) {
    return getIssueRule(issue, entity) === 'R8'
        && getR8EntityCategory(entity) === 'UNMET';
}

export function isR10Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R10';
}

export function isR11Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R11';
}

export function buildR11PlateTargets(plate, t) {
    const plateName = plate?.name || extractEntityCodeName(plate).name || t('relatorio_entidade_prato');
    const plateId = plate?.id || null;
    const dias = Array.isArray(plate?.dias) ? plate.dias : [];
    const targets = dias.map((dia) => buildHighlightTarget(
        dia?.date,
        dia?.id_coluna,
        plateId,
        'usage',
        {
            reasonLabel: t('relatorio_highlight_utilizacao_de', { PARAM: plateName }),
            reasonKey: `R11:usage:${plateId || plateName}`,
            origin: 'action'
        }
    ));
    return dedupeTargets(targets);
}

export function isR9Entity(issue, entity) {
    return getIssueRule(issue, entity) === 'R9';
}

export function getR8EntityCategory(entity) {
    const normalized = String(entity?.category || '').trim().toUpperCase();
    if (normalized === 'UNMET' || normalized === 'UNREALIZED') return normalized;
    return String(entity?.scope || '').toUpperCase() === 'MONTH' ? 'UNREALIZED' : 'UNMET';
}

export function getR8CategoryLabel(category) {
    if (category === 'UNMET') return 'Não realizado';
    if (category === 'UNREALIZED') return 'Não realizado no mês';
    return '-';
}

export function getR8EntityGroups(issue) {
    const entities = getVisibleEntities(issue)
        .filter(entity => getIssueRule(issue, entity) === 'R8');

    const byCategory = new Map();
    entities.forEach((entity, entityIndex) => {
        const category = getR8EntityCategory(entity);
        const current = byCategory.get(category) || [];
        current.push({ entity, entityIndex });
        byCategory.set(category, current);
    });

    const categoryOrder = ['UNMET', 'UNREALIZED'];
    return categoryOrder
        .filter(category => byCategory.has(category))
        .map((category) => ({
            groupKey: category,
            label: getR8CategoryLabel(category),
            entities: byCategory.get(category).map(entry => entry.entity),
        }));
}

export function hasR8GroupedEntities(issue) {
    return getR8EntityGroups(issue).length > 0;
}

export function getR8GroupStorageKey(issue, issueIndex, groupKey) {
    return `${getIssueKey(issue, issueIndex)}::R8::${groupKey}`;
}

export function getEntityLabel(entity) {
    if (!entity) return '-';
    if (typeof entity === 'string' || typeof entity === 'number') {
        return formatTokenLabel(String(entity));
    }
    const parsed = extractEntityCodeName(entity);
    const shouldShowCode = shouldShowEntityCode(entity);
    if (shouldShowCode && parsed.code !== '-' && parsed.name !== '-') return `${parsed.code} | ${parsed.name}`;
    if (parsed.name !== '-') return parsed.name;
    if (shouldShowCode && parsed.code !== '-') return parsed.code;
    return '-';
}

export function shouldShowEntityCode(entity) {
    if (!entity) return false;

    const normalize = (value) => String(value || '').trim();

    if (typeof entity === 'string' || typeof entity === 'number') {
        const raw = normalize(entity).toLowerCase();
        return raw.startsWith('prato:');
    }

    const entityType = normalize(entity.entityType || entity.type || entity.kind).toUpperCase();
    if (entityType) {
        return entityType === 'PRATO' || entityType === 'PLATE';
    }

    const label = normalize(entity.label).toLowerCase();
    if (label.startsWith('prato:')) return true;

    const id = normalize(entity.id);
    return /^P\d+$/i.test(id);
}

export function getR10ColumnContext(entity) {
    if (!entity || typeof entity !== 'object') return null;

    if (entity.columnContext && typeof entity.columnContext === 'object') {
        return entity.columnContext;
    }

    if (entity.column && typeof entity.column === 'object') {
        return entity.column;
    }

    return null;
}

export function getR10DishTypeName(entity) {
    const context = getR10ColumnContext(entity);
    if (!context || typeof context.dishTypeName !== 'string') return null;
    const normalized = context.dishTypeName.trim();
    return normalized || null;
}

export function getR10DishGroups(entity) {
    const context = getR10ColumnContext(entity);
    if (!context || !Array.isArray(context.dishGroups)) return [];

    return context.dishGroups
        .map((group) => formatTokenLabel(String(group || '').trim()))
        .filter((group) => group && group !== '-');
}

export function getR8RequiredEntityLabel(entity, t) {
    const required = entity?.requiredItem
        || (Array.isArray(entity?.missingItems) && entity.missingItems.length === 1 ? entity.missingItems[0] : null);
    if (!required) {
        return t('relatorio_entidade_item_obrigatorio');
    }
    const entityType = String(required?.entityType || '').trim().toUpperCase();
    if (entityType === 'PRATO' || entityType === 'PLATE') {
        return t('relatorio_entidade_prato_obrigatorio');
    }
    if (entityType === 'GRUPO' || entityType === 'GROUP') {
        return t('relatorio_entidade_grupo_obrigatorio');
    }
    if (isPlateEntity(required)) {
        return t('relatorio_entidade_prato_obrigatorio');
    }
    return t('relatorio_entidade_item_obrigatorio');
}

function isPlateEntity(entity) {
    if (!entity) return false;
    const id = String(entity.id || '');
    return /^P\d+$/i.test(id);
}

export function isSameR9Entity(target, entityRef) {
    if (!target || !entityRef) return false;

    const targetPlateId = normalizePlateId(target?.plateId);
    const entityId = normalizePlateId(entityRef?.id);
    const entityType = String(entityRef?.entityType || '').trim().toUpperCase();

    if (entityType === 'PRATO' && targetPlateId && entityId && targetPlateId === entityId) {
        return true;
    }

    const targetReason = normalizeReasonLabel(target?.reason);
    if (!targetReason) return false;

    const targetParsed = extractEntityCodeName(targetReason);
    const entityParsed = extractEntityCodeName(entityRef);

    if (entityId && targetParsed.code !== '-' && targetParsed.code === entityId) {
        return true;
    }

    const targetName = (targetParsed.name || '').trim().toUpperCase();
    const entityName = (entityParsed.name || '').trim().toUpperCase();
    if (targetName && entityName && targetName === entityName) {
        return true;
    }

    return false;
}

export function getR9ConflictTargetName(target) {
    const rawReason = normalizeReasonLabel(target?.reason);
    if (!rawReason) return null;
    const parsed = extractEntityCodeName(rawReason);
    if (parsed.name && parsed.name !== '-') return parsed.name;
    const fallback = formatTokenLabel(rawReason);
    return fallback !== '-' ? fallback : null;
}

export function getR9ConflictCounterpartName(entity, target, t) {
    const leftName = extractEntityCodeName(entity?.left).name || t('relatorio_entidade_lado_esquerdo');
    const rightName = extractEntityCodeName(entity?.right).name || t('relatorio_entidade_lado_direito');

    if (isSameR9Entity(target, entity?.left)) return rightName;
    if (isSameR9Entity(target, entity?.right)) return leftName;

    return getR9ConflictTargetName(target) || rightName || leftName;
}

// ====== getEntityView ======

export function getEntityView(issue, entity, t) {
    const ruleId = getIssueRule(issue, entity);
    const view = {
        ruleId,
        rows: [],
        tags: [],
        lines: [],
        actions: [],
        fallback: false
    };

    switch (ruleId) {
        case 'R1':
            view.rows = [
                buildEntityRow(t('relatorio_entidade_prato'), entity.plate, {
                    singleLine: true
                }),
                {
                    label: t('relatorio_entidade_criterio'),
                    value: formatCriterion(entity.criterion, t),
                    valueTitle: formatCriterion(entity.criterion, t),
                    singleLine: true
                },
                buildUsageRow(t('relatorio_entidade_uso'), entity.usage)
            ];
            view.lines = (entity.occurrences || []).map(occ => ({
                type: 'occurrence',
                date: formatDate(occ.date),
                sequencial: occ.sequencial || '-',
                action: occ.date ? {
                    date: occ.date,
                    sequencial: occ.sequencial,
                    plateId: entity.plate?.id || null,
                    highlightType: 'usage'
                } : null
            }));
            if (['MAX_MONTH', 'MAX_WEEK'].includes(entity.criterion)) {
                const usageTargets = buildR1UsageTargets(entity, t);
                if (usageTargets.length) {
                    view.actions.push({
                        label: t('relatorio_destacar_utilizacoes'),
                        highlightType: 'usage',
                        highlightTargets: usageTargets
                    });
                }
            }
            if (['MIN_MONTH', 'ZERO_USAGE'].includes(entity.criterion)) {
                const candidateTargets = buildR1CandidateTargets(entity, t);
                if (candidateTargets.length) {
                    view.actions.push({
                        label: t('relatorio_destacar_oportunidades'),
                        highlightType: 'candidate',
                        highlightTargets: candidateTargets
                    });
                }
            }
            break;

        case 'R2':
            view.rows = [
                { label: t('relatorio_entidade_categoria'), value: formatCategory(entity.category) },
                { label: t('relatorio_entidade_ocorrencias'), value: entity.count || 0 }
            ];
            {
                const rawGroups = Array.isArray(entity.groups) ? entity.groups : [];
                const preparedGroups = rawGroups.map((groupEntry, groupIndex) => {
                    const usageTargets = buildR2UsageTargets(groupEntry, t);
                    const candidateTargets = buildR2CandidateTargets(groupEntry, t);
                    return {
                        groupEntry,
                        groupIndex,
                        usageTargets,
                        candidateTargets
                    };
                });

                if (entity.category === 'NON_USED') {
                    preparedGroups.sort((left, right) => {
                        const leftHasCandidate = left.candidateTargets.length > 0 ? 1 : 0;
                        const rightHasCandidate = right.candidateTargets.length > 0 ? 1 : 0;
                        if (leftHasCandidate !== rightHasCandidate) {
                            return rightHasCandidate - leftHasCandidate;
                        }
                        return left.groupIndex - right.groupIndex;
                    });
                }

                view.lines = preparedGroups.map((prepared) => {
                    const groupEntry = prepared.groupEntry;
                    const groupInfo = extractEntityCodeName(groupEntry.group);
                    const groupName = groupEntry.groupName || groupInfo.name || '-';
                    const groupCode = groupEntry.groupCode || groupEntry.group?.id || null;
                    return {
                        type: 'group-usage',
                        groupKey: groupCode || `group-${prepared.groupIndex}`,
                        groupIndex: prepared.groupIndex,
                        name: groupName,
                        scope: groupEntry.periodLabel || groupEntry.scopeLabel || formatScope(groupEntry.scope, groupEntry.week),
                        used: groupEntry.usage?.used ?? 0,
                        min: groupEntry.usage?.min ?? '-',
                        max: groupEntry.usage?.max ?? '-',
                        delta: groupEntry.usage?.delta ?? null,
                        actions: (() => {
                            const actions = [];
                            if (prepared.usageTargets.length) {
                                actions.push({
                                    label: t('relatorio_destacar_utilizacoes'),
                                    highlightType: 'usage',
                                    highlightTargets: prepared.usageTargets
                                });
                            }
                            if (['NON_USED', 'BELOW_MIN'].includes(entity.category) && prepared.candidateTargets.length) {
                                actions.push({
                                    label: t('relatorio_destacar_oportunidades'),
                                    highlightType: 'candidate',
                                    highlightTargets: prepared.candidateTargets
                                });
                            }
                            return actions;
                        })()
                    };
                });
            }
            break;

        case 'R3':
            if (isR3ByTypeEntity(entity)) {
                view.rows = [
                    {
                        label: t('relatorio_entidade_tipo'),
                        value: entity.tipo || '-',
                        valueTitle: entity.tipo || '-',
                        singleLine: true
                    },
                    {
                        label: t('date'),
                        value: formatDate(entity.date),
                        valueTitle: formatDate(entity.date),
                        singleLine: true
                    }
                ];
            } else {
                view.rows = [
                    {
                        label: t('date'),
                        value: formatDate(entity.date),
                        valueTitle: formatDate(entity.date),
                        singleLine: true
                    }
                ];
            }
            view.tags = buildR3Tags(entity, t);
            {
                const allTargets = buildR3AllTargets(entity, view.tags);
                if (allTargets.length) {
                    view.actions.push({
                        label: t('relatorio_destacar_conflitos'),
                        highlightType: 'conflict',
                        highlightTargets: allTargets
                    });
                } else if (entity.date) {
                    view.actions.push({
                        date: entity.date,
                        highlightType: 'conflict'
                    });
                }
            }
            break;

        case 'R4':
            {
                const ingredientName = extractEntityCodeName(entity?.ingredient).name || getEntityLabel(entity.ingredient);
                const previousReasonLabel = t('relatorio_r4_reason_previous_day', { PARAM: ingredientName });
                const currentReasonLabel = t('relatorio_r4_reason_consecutive_day', { PARAM: ingredientName });
                const previousTargets = entity.previousDate
                    ? buildR4Targets(
                        entity.previousDate,
                        entity.previousPlates,
                        entity.previousSequentials,
                        {
                            reasonLabel: previousReasonLabel,
                            reasonKey: `R4:${entity.previousDate}:previous`,
                            origin: 'action'
                        }
                    )
                    : [];
                const currentTargets = entity.date
                    ? buildR4Targets(
                        entity.date,
                        entity.currentPlates,
                        entity.currentSequentials,
                        {
                            reasonLabel: currentReasonLabel,
                            reasonKey: `R4:${entity.date}:current`,
                            origin: 'action'
                        }
                    )
                    : [];

                const combinedTargets = dedupeTargets([...previousTargets, ...currentTargets]);
                if (combinedTargets.length) {
                    const firstTarget = combinedTargets[0];
                    const action = {
                        label: t('relatorio_destacar_repeticao'),
                        date: entity.previousDate || entity.date || null,
                        sequencial: firstTarget?.sequencial || null,
                        plateId: firstTarget?.plateId || null,
                        highlightType: 'conflict',
                        highlightTargets: combinedTargets,
                        focusTarget: firstTarget
                    };
                    view.lines = [{
                        type: 'r4-details',
                        items: [
                            buildEntityRow(t('relatorio_entidade_ingrediente'), entity.ingredient, {
                                codeLabel: false,
                                singleLine: true
                            }),
                            {
                                label: t('relatorio_entidade_tipo'),
                                type: 'text',
                                value: entity.tipo || '-',
                                valueTitle: entity.tipo || '-'
                            },
                            {
                                label: t('date'),
                                type: 'text',
                                value: `${formatDate(entity.previousDate)} → ${formatDate(entity.date)}`,
                                valueTitle: `${formatDate(entity.previousDate)} → ${formatDate(entity.date)}`
                            }
                        ],
                        action
                    }];
                }
            }
            break;

        case 'R5':
            view.rows = [
                { label: t('relatorio_entidade_complexidade'), value: entity.complexityLevel || '-' },
                buildUsageRow(t('relatorio_entidade_uso'), entity.usage)
            ];
            view.lines = (Array.isArray(entity.contexts) ? entity.contexts : []).map((context, contextIndex) => {
                const highlightTargets = buildR5ContextTargets(context, t);
                const action = highlightTargets.length
                    ? {
                        label: t('relatorio_destacar_incidencias'),
                        highlightType: 'usage',
                        highlightTargets,
                        focusTarget: highlightTargets[0] || null
                    }
                    : null;
                return {
                    type: 'r5-details',
                    items: [
                        {
                            label: t('relatorio_entidade_tipo'),
                            value: context?.tipo || '-',
                            valueTitle: context?.tipo || '-'
                        },
                        {
                            label: t('date'),
                            value: formatDate(context?.date),
                            valueTitle: formatDate(context?.date)
                        },
                        {
                            label: t('relatorio_alta'),
                            value: `${context?.altaCount ?? 0}/${context?.totalCount ?? 0}`,
                            valueTitle: `${context?.altaCount ?? 0}/${context?.totalCount ?? 0}`
                        },
                        {
                            label: t('relatorio_limite'),
                            value: formatPercent(context?.threshold),
                            valueTitle: formatPercent(context?.threshold)
                        }
                    ],
                    action,
                    key: `r5-${contextIndex}`
                };
            });
            break;

        case 'R6':
            view.rows = [
                { label: t('date'), value: formatDate(entity.date) },
                buildEntityRow(t('relatorio_entidade_coluna'), entity.column),
                buildEntityRow(t('relatorio_entidade_prato'), entity.plate),
                { label: t('pre_preparation'), value: formatBoolean(entity.dayAllowsPrePrep) }
            ];
            if (entity.date) view.actions.push({ date: entity.date, sequencial: entity.column?.id, plateId: entity.plate?.id, highlightType: 'conflict' });
            break;

        case 'R7':
            view.rows = [
                { label: t('date'), value: formatDate(entity.date) },
                buildEntityRow(t('group_of_dishes'), entity.group),
                buildEntityRow(t('plates'), entity.plate)
            ];
            view.tags = entity.restrictionsTriggered || [];
            if (entity.date) view.actions.push({ date: entity.date, plateId: entity.plate?.id, highlightType: 'conflict' });
            break;

        case 'R8':
            if (entity.scope === 'MONTH') {
                const r8Category = getR8EntityCategory(entity);
                const requiredLabel = getR8RequiredEntityLabel(entity, t);
                if (r8Category === 'UNREALIZED') {
                    view.rows = [
                        buildEntityRow(t('relatorio_entidade_pivo'), entity.pivot, {
                            codeLabel: false,
                            singleLine: true
                        }),
                        buildEntityRow(requiredLabel, entity.requiredItem, {
                            codeLabel: false,
                            singleLine: true
                        })
                    ];
                } else {
                    view.rows = [
                        buildEntityRow(t('relatorio_entidade_pivo'), entity.pivot, {
                            codeLabel: false,
                            singleLine: true
                        }),
                        { label: t('date'), value: formatDate(entity.date) }
                    ];
                }
                if (r8Category !== 'UNREALIZED') {
                    view.tags = (entity.missingItems || []).map(item => getEntityLabel(item));
                }
                const candidateTargets = buildR8CandidateTargets(entity, 'pivot', t);
                if (candidateTargets.length) {
                    view.actions.push({
                        label: t('relatorio_destacar_oportunidades'),
                        highlightType: 'candidate',
                        highlightTargets: candidateTargets
                    });
                }
            } else {
                view.rows = [
                    buildEntityRow(t('relatorio_entidade_pivo'), entity.pivot, {
                        codeLabel: false,
                        singleLine: true
                    }),
                    { label: t('date'), value: formatDate(entity.date) }
                ];
                view.tags = (entity.missingItems || []).map(item => getEntityLabel(item));
                const candidateTargets = buildR8CandidateTargets(entity, 'required', t);
                if (candidateTargets.length) {
                    view.actions.push({
                        label: t('relatorio_r8_destacar_oportunidades_obrigatorio'),
                        highlightType: 'candidate',
                        highlightTargets: candidateTargets
                    });
                }
            }
            break;

        case 'R9':
            {
                const conflictTargets = buildR9ConflictTargets(entity, t);
                const action = conflictTargets.length
                    ? {
                        label: t('relatorio_destacar_conflito'),
                        highlightType: 'conflict',
                        highlightTargets: conflictTargets
                    }
                    : (entity.date ? {
                        label: t('relatorio_destacar_conflito'),
                        date: entity.date,
                        highlightType: 'conflict'
                    } : null);

                view.lines = [
                    {
                        type: 'r9-summary',
                        items: [
                            {
                                label: t('relatorio_entidade_conflito'),
                                value: formatR9ConflictType(entity.conflictType)
                            },
                            {
                                label: t('date'),
                                value: formatDate(entity.date)
                            }
                        ]
                    },
                    {
                        type: 'r9-details',
                        items: [
                            buildEntityRow(t('relatorio_entidade_lado_esquerdo'), entity.left, {
                                codeLabel: false,
                                singleLine: true
                            }),
                            buildEntityRow(t('relatorio_entidade_lado_direito'), entity.right, {
                                codeLabel: false,
                                singleLine: true
                            })
                        ],
                        action
                    }
                ];
            }
            break;

        case 'R10':
            {
                const dishTypeName = getR10DishTypeName(entity);
                view.rows = [
                    {
                        label: t('relatorio_entidade_tipo'),
                        value: dishTypeName || '-',
                        valueTitle: dishTypeName || '-',
                        singleLine: true
                    },
                    { label: t('date'), value: formatDate(entity.date) },
                    { label: t('relatorio_entidade_coluna'), value: formatColumnDisplay(entity.column) }
                ];

                view.tags = getR10DishGroups(entity);
            }
            if (entity.date) {
                const occurrenceTargets = buildTargetsFromIssueTargets([
                    {
                        date: entity.date,
                        sequencial: entity.column?.id,
                        plateId: null
                    }
                ], null, 'usage');
                view.actions.push({
                    label: t('relatorio_exibir_ocorrencia'),
                    date: entity.date,
                    sequencial: entity.column?.id,
                    highlightType: 'usage',
                    highlightTargets: occurrenceTargets
                });
            }
            break;

        case 'R11':
            {
                const meta = Number(entity?.meta);
                view.rows = [
                    {
                        label: t('relatorio_r11_pu_acumulado'),
                        value: formatCurrency(entity?.puAcumulado),
                        valueTitle: formatCurrency(entity?.puAcumulado),
                        singleLine: true
                    },
                    {
                        label: t('relatorio_r11_meta'),
                        value: Number.isFinite(meta) ? formatCurrency(meta) : '-',
                        valueTitle: Number.isFinite(meta) ? formatCurrency(meta) : '-',
                        singleLine: true
                    }
                ];

                const topPlates = Array.isArray(entity?.topPlates) ? entity.topPlates : [];
                view.lines = topPlates.map((plate, plateIndex) => {
                    const plateTargets = buildR11PlateTargets(plate, t);
                    const action = plateTargets.length
                        ? {
                            label: t('relatorio_destacar_utilizacoes'),
                            highlightType: 'usage',
                            highlightTargets: plateTargets,
                            focusTarget: plateTargets[0] || null
                        }
                        : null;
                    return {
                        type: 'r11-plate',
                        plateIndex,
                        items: [
                            buildEntityRow(t('relatorio_entidade_prato'), { id: plate?.id, name: plate?.name }, {
                                singleLine: true
                            }),
                            {
                                label: t('relatorio_r11_custo_per_capita'),
                                value: formatCurrency(plate?.costPerCapita),
                                valueTitle: formatCurrency(plate?.costPerCapita)
                            },
                            {
                                label: t('relatorio_r11_dias'),
                                value: (Array.isArray(plate?.dias) ? plate.dias : [])
                                    .map(dia => formatDate(dia?.date))
                                    .filter(Boolean)
                                    .join(', ') || '-',
                                valueTitle: (Array.isArray(plate?.dias) ? plate.dias : [])
                                    .map(dia => formatDate(dia?.date))
                                    .filter(Boolean)
                                    .join(', ') || '-'
                            }
                        ],
                        action
                    };
                });
            }
            break;

        default:
            view.fallback = true;
            break;
    }

    return view;
}

export function getIssueDescription(issue, t) {
    const rawDescription = issue?.description || '';
    if (!rawDescription) return '-';

    const hasR1ZeroUsage = Array.isArray(issue?.affectedEntities)
        && issue.affectedEntities.some((entity) => {
            const ruleId = getIssueRule(issue, entity);
            return ruleId === 'R1' && String(entity?.criterion || '').toUpperCase() === 'ZERO_USAGE';
        });

    if (!hasR1ZeroUsage) return rawDescription;

    return String(rawDescription)
        .replace(/foi utilizado com zero uso/gi, 'não foi utilizado')
        .replace(/utilizado com zero uso/gi, 'sem utilização');
}

export function stringifyEntity(entity) {
    try {
        return JSON.stringify(entity, null, 2);
    } catch {
        return String(entity);
    }
}

export function getTagLabel(tag, t) {
    if (tag && typeof tag === 'object' && typeof tag.label === 'string') {
        return tag.label;
    }
    return String(tag ?? '-');
}

export function isActionableTag(tag) {
    if (!tag || typeof tag !== 'object') return false;
    const hasTargets = Array.isArray(tag.highlightTargets) && tag.highlightTargets.length > 0;
    const hasAction = !!tag.action;
    return hasTargets || hasAction;
}

export function getR3EntityActions(entity, t) {
    const tags = buildR3Tags(entity, t);
    const allTargets = buildR3AllTargets(entity, tags);
    if (allTargets.length) {
        return [{
            label: t('relatorio_destacar_conflitos'),
            highlightType: 'conflict',
            highlightTargets: allTargets
        }];
    }
    if (entity?.date) {
        return [{
            date: entity.date,
            highlightType: 'conflict'
        }];
    }
    return [];
}

// ====== Identificação para Problemas Ignorados (nível affectedEntity / group) ======
export function getEntityKey(issue, issueIndex, entity, entityIndex) {
    const issueKey = getIssueKey(issue, issueIndex);
    return `${issueKey}::entity-${entityIndex}`;
}

export function getGroupKey(entity, group, groupIndex) {
    const code = group?.groupCode || group?.group?.id || null;
    return code !== null && code !== undefined && String(code).trim().length
        ? String(code)
        : `group-${groupIndex}`;
}